import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { NoteCard } from '../components/note/NoteCard';
import { Icon } from '../components/ui/Icon';
import { Switch, confirmDialog } from '../components/ui/ui';
import type { NoteEditing } from '../editor/useNoteEditing';
import { uid } from '../lib/id';
import { store, useStore } from '../state/store';
import type { Design, Note } from '../storage/schemas';
import { designsInSection, ORIGINAL_SECTIONS, REFERENCE_SECTIONS, SECTION_INFO } from './catalog';
import { getFont } from './fonts';
import { designLook, resolveLook } from './look';
import { DesignEditorSheet } from './DesignEditorSheet';
import './gallery.css';

const ORIGINAL_INFO: Record<string, string> = {
  Minimal: 'MemoNest original · quiet basics',
  Pastel: 'MemoNest original · soft pastels',
  Night: 'MemoNest original · night colours',
  Study: 'MemoNest original · notebooks and graph paper',
  Productivity: 'MemoNest original · clear and focused',
  Colourful: 'MemoNest original · loud and bright',
  Seasonal: 'MemoNest original · for every season',
  Typography: 'MemoNest original · type-led looks',
  Decorative: 'MemoNest original · tape, pins and frames',
};

interface TileProps {
  design: Design;
  note: Pick<Note, 'id' | 'lines' | 'style' | 'images' | 'strokes'>;
  selected: boolean;
  favorite: boolean;
  onApply: (d: Design) => void;
  onMenu: (d: Design, action: string) => void;
}

const Tile = memo(function Tile({ design, note, selected, favorite, onApply, onMenu }: TileProps) {
  const [menu, setMenu] = useState(false);
  const preview = useMemo(() => ({ ...note, images: [], strokes: [], style: { ...note.style, compact: false } }), [note]);
  const look = designLook(design);
  const clear = design.paperAlpha !== undefined && design.paperAlpha < 1;
  return (
    <li className={`dg-tile ${selected ? 'is-selected' : ''} ${clear ? 'is-clear' : ''}`}>
      <button type="button" className="dg-apply" onClick={() => onApply(design)} aria-pressed={selected} aria-label={`${design.name} (${design.category})${selected ? ', in use' : ''}`}>
        <NoteCard note={preview} look={look} />
      </button>
      <span className="dg-tag" aria-hidden="true">
        {design.name}
      </span>
      {favorite && <Icon name="star" size={14} className="dg-fav" aria-label="Favourite" />}
      <button type="button" className="dg-more" aria-label={`More for ${design.name}`} aria-expanded={menu} onClick={() => setMenu((m) => !m)}>
        <Icon name="more" size={22} />
      </button>
      {menu && (
        <div className="dg-menu" role="menu" onMouseLeave={() => setMenu(false)}>
          {[
            ['favorite', favorite ? 'Remove favourite' : 'Favourite'],
            ['customize', 'Customise a copy'],
            ...(design.source === 'custom'
              ? [
                  ['edit', 'Edit'],
                  ['rename', 'Rename'],
                  ['duplicate', 'Duplicate'],
                  ['delete', 'Delete'],
                ]
              : []),
          ].map(([a, label]) => (
            <button
              key={a}
              type="button"
              role="menuitem"
              className={a === 'delete' ? 'is-danger' : ''}
              onClick={() => {
                setMenu(false);
                onMenu(design, a);
              }}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </li>
  );
});

/** Reference: full-screen navy gallery, ✕ at top-left, sections of two-column tiles tagged #dXXX. */
export function DesignGallery({ open, onClose, note, editing }: { open: boolean; onClose: () => void; note: Note; editing: NoteEditing }) {
  const custom = useStore((s) => s.designs);
  const favorites = useStore((s) => s.settings.favoriteDesigns);
  const [query, setQuery] = useState('');
  const [onlyFav, setOnlyFav] = useState(false);
  const [editor, setEditor] = useState<Design | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !editor) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, editor]);

  const apply = (d: Design) =>
    editing.edit(
      (n) => ({ ...n, designId: d.id, style: { ...n.style, paper: undefined, ink: undefined, line: undefined, accent: undefined, pattern: undefined, font: undefined } }),
      'design',
    );

  const nextCustomName = () => `#c${String(custom.length + 1).padStart(3, '0')}`;

  const onMenu = async (d: Design, action: string) => {
    switch (action) {
      case 'favorite':
        store.toggleFavoriteDesign(d.id);
        break;
      case 'customize':
      case 'duplicate':
        setEditor({ ...d, id: uid('cd'), name: nextCustomName(), category: 'Custom', source: 'custom', createdAt: Date.now(), updatedAt: Date.now() });
        break;
      case 'edit':
        setEditor(d);
        break;
      case 'rename': {
        const name = window.prompt('Design name', d.name)?.trim();
        if (name) await store.saveDesign({ ...d, name: name.slice(0, 60), updatedAt: Date.now() });
        break;
      }
      case 'delete':
        if (await confirmDialog({ title: `Delete ${d.name}?`, message: 'Notes using it go back to the default design.', confirmLabel: 'Delete', danger: true })) await store.deleteDesign(d.id);
        break;
    }
  };

  const q = query.trim().toLowerCase();
  const match = (d: Design) =>
    (!onlyFav || favorites.includes(d.id)) && (!q || `${d.name} ${d.category} ${d.pattern} ${getFont(d.font).label} ${d.paper}`.toLowerCase().includes(q));

  if (!open) return null;
  const look = resolveLook(note, custom);
  const hasOverrides = (['paper', 'ink', 'line', 'accent', 'pattern', 'font'] as const).some((k) => note.style[k] !== undefined);

  const section = (title: string, list: Design[], info?: string, badge?: string) => {
    const shown = list.filter(match);
    if (!shown.length) return null;
    return (
      <section key={title} className="dg-section" aria-labelledby={`dg-${title}`}>
        <h2 id={`dg-${title}`}>
          {title}
          {badge && <span className="dg-badge">{badge}</span>}
        </h2>
        {info && <p className="dg-info">{info}</p>}
        <ul className="dg-grid">
          {shown.map((d) => (
            <Tile key={d.id} design={d} note={note} selected={d.id === note.designId} favorite={favorites.includes(d.id)} onApply={apply} onMenu={onMenu} />
          ))}
        </ul>
      </section>
    );
  };

  return (
    <div className="dg-root no-print" role="dialog" aria-modal="true" aria-label="Designs" ref={ref}>
      <button ref={closeRef} type="button" className="dg-close" aria-label="Close designs" onClick={onClose}>
        <Icon name="close" size={26} strokeWidth={2.6} />
      </button>
      <div className="dg-scroll">
        <div className="dg-rows">
          <div className="dg-row">
            <Icon name="compact" size={26} />
            <span>Compact notes</span>
            <Switch label="Compact notes" checked={note.style.compact} onChange={(v) => editing.edit((n) => ({ ...n, style: { ...n.style, compact: v } }))} />
          </div>
          <div className="dg-row dg-search">
            <Icon name="search" size={22} />
            <input type="search" placeholder="Search designs" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Search designs" />
            <button type="button" className={`dg-chip ${onlyFav ? 'is-on' : ''}`} aria-pressed={onlyFav} onClick={() => setOnlyFav((v) => !v)}>
              <Icon name="star" size={16} /> Favourites
            </button>
          </div>
          {hasOverrides && (
            <div className="dg-row">
              <Icon name="undo" size={22} />
              <span>This note has custom colours</span>
              <button type="button" className="dg-chip" onClick={() => apply({ ...look, id: note.designId } as Design)}>
                Reset
              </button>
            </div>
          )}
        </div>

        <section className="dg-section" aria-labelledby="dg-mine">
          <h2 id="dg-mine">My Designs</h2>
          <p className="dg-info">Make your own: paper, text and line colours, pattern and font.</p>
          <ul className="dg-grid">
            <li className="dg-tile">
              <button
                type="button"
                className="dg-add"
                aria-label="Create a design"
                onClick={() =>
                  setEditor({ id: uid('cd'), name: nextCustomName(), category: 'Custom', source: 'custom', ...look, patternScale: look.patternScale, createdAt: Date.now(), updatedAt: Date.now() })
                }
              >
                <Icon name="plus" size={34} strokeWidth={2.4} />
              </button>
            </li>
            {custom.filter(match).map((d) => (
              <Tile key={d.id} design={d} note={note} selected={d.id === note.designId} favorite={favorites.includes(d.id)} onApply={apply} onMenu={onMenu} />
            ))}
          </ul>
        </section>

        {REFERENCE_SECTIONS.map((t) => section(t, designsInSection(t), SECTION_INFO[t], t === 'Clear' ? 'NEW' : undefined))}
        <h2 className="dg-group">MemoNest originals</h2>
        {ORIGINAL_SECTIONS.map((t) => section(t, designsInSection(t), ORIGINAL_INFO[t]))}
        <p className="dg-foot">Colours of the first sections follow the original LockScreenNotes designs. “MemoNest originals” are new.</p>
      </div>
      {editor && (
        <DesignEditorSheet
          design={editor}
          note={note}
          onClose={() => setEditor(null)}
          onSave={async (d) => {
            await store.saveDesign({ ...d, updatedAt: Date.now() });
            apply(d);
            setEditor(null);
            store.toast(`Saved ${d.name}.`, 'success');
          }}
        />
      )}
    </div>
  );
}
